/**
 * Tools (Import / Export) type definitions (F3、Phase 5)。
 *
 * Bitwarden JSON エクスポート仕様の subset と OoKey native フォーマットを定義。
 * 1Password / LastPass / KeePass / CSV は未対応 (defer)。
 */

import type { VaultItemPayload } from '@features/vault/types'

// ── Bitwarden JSON export 仕様 (encrypted: false のみサポート) ─────────

export interface BitwardenLogin {
  readonly username?: string | null
  readonly password?: string | null
  /** Bitwarden の URI 行。`match` は 0=domain/1=host/2=startsWith/3=exact/4=regex/5=never。 */
  readonly uris?: readonly { uri: string; match?: number | null }[] | null
  readonly totp?: string | null
}

/** Bitwarden のカスタムフィールド。`type` は 0=Text / 1=Hidden / 2=Boolean / 3=Linked。 */
export interface BitwardenField {
  readonly name?: string | null
  readonly value?: string | null
  readonly type?: number
}

export interface BitwardenCard {
  readonly cardholderName?: string | null
  readonly number?: string | null
  readonly expMonth?: string | null
  readonly expYear?: string | null
  readonly code?: string | null
}

export interface BitwardenIdentity {
  readonly firstName?: string | null
  readonly lastName?: string | null
  readonly email?: string | null
  readonly phone?: string | null
}

export interface BitwardenSecureNote {
  readonly type?: number
}

export interface BitwardenFolder {
  readonly id: string
  readonly name: string
}

/** Bitwarden item type の数値: 1=login / 2=secureNote / 3=card / 4=identity */
export type BitwardenItemType = 1 | 2 | 3 | 4

export interface BitwardenItem {
  readonly id?: string
  readonly folderId?: string | null
  readonly type: BitwardenItemType
  readonly name: string
  readonly notes?: string | null
  readonly favorite?: boolean
  readonly login?: BitwardenLogin | null
  readonly card?: BitwardenCard | null
  readonly identity?: BitwardenIdentity | null
  readonly secureNote?: BitwardenSecureNote | null
  /** 全 type 共通のユーザー定義フィールド。 */
  readonly fields?: readonly BitwardenField[] | null
}

export interface BitwardenExport {
  readonly encrypted: false
  readonly folders?: readonly BitwardenFolder[]
  readonly items: readonly BitwardenItem[]
}

// ── OoKey native JSON export shape ─────────────────────────────────────

export interface OoKeyExportFolder {
  readonly id: string
  readonly name: string
}

export interface OoKeyExportItem {
  readonly folderId: string | null
  readonly payload: VaultItemPayload
}

export interface OoKeyExport {
  readonly format: 'ookey'
  readonly version: 1
  readonly exportedAt: string
  readonly folders: readonly OoKeyExportFolder[]
  readonly items: readonly OoKeyExportItem[]
}

// ── Google Password Manager JSON ─────────────────────────────────────
// Chrome の password export 公式は CSV だが、サードパーティ製ツール (Bitwarden CLI 等)
// が Google 形式を JSON 化することがある。OoKey は以下の最小 shape を受け付ける:
//   - top-level array: `[{ name, url, username, password }, ...]`
//   - object: `{ passwords: [{...}, ...] }` (alternate)
// 各行は login type にマップする (Google export には card/note は無い)。

export interface GoogleLoginRow {
  readonly name?: string | null
  readonly url?: string | null
  readonly username?: string | null
  readonly password?: string | null
  readonly note?: string | null
}

export interface GoogleExportObject {
  readonly passwords: readonly GoogleLoginRow[]
}

// ── Import フォーマット推定 ────────────────────────────────────────────

export type ImportFormat = 'ookey' | 'bitwarden' | 'google' | 'csv'

export interface ImportPreviewItem {
  /** 表示用 name */
  readonly name: string
  /** OoKey 内部の VaultItemPayload に正規化済 */
  readonly payload: VaultItemPayload
  /** Bitwarden の folderId は import 時点で OoKey 側 folderId に解決済の値 */
  readonly folderId: string | null
}

export interface ImportPreview {
  readonly format: ImportFormat
  readonly items: readonly ImportPreviewItem[]
  /** 新規作成すべきフォルダ (名前のみ、id は import 時に発行) */
  readonly newFolderNames: readonly string[]
}

export type ImportMessageKey =
  | 'unknown_format'
  | 'invalid_json'
  | 'encrypted_not_supported'
  | 'empty'
  | 'generic'

export class ImportError extends Error {
  public constructor(public readonly messageKey: ImportMessageKey) {
    super(messageKey)
    this.name = 'ImportError'
  }
}
